import { cx } from "../../lib/cx.js";
import { Icon } from "../Icon/Icon.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import { useControllable } from "../../lib/useControllable.js";
import { useFormFieldControl } from "../FormField/FormFieldContext.js";
import { ListboxPopup, filterOptions, nextActive } from "../_listbox/Listbox.js";
/* empty css                */
import { forwardRef, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
//#region src/components/MultiSelect/MultiSelect.tsx
function setRef(ref, value) {
	if (typeof ref === "function") ref(value);
	else if (ref) ref.current = value;
}
/**
* A multi-value combobox: type to filter, pick options to toggle them, and remove them
* as tags. The ref goes to the text field; `className` goes on the box.
*/
var MultiSelect = forwardRef(function MultiSelect({ options, value, onChange, placeholder = "Select…", size = "md", invalid: invalidProp, disabled = false, emptyMessage = "No matches", id: idProp, className, open: openProp, defaultOpen = false, onOpenChange, inputValue: inputValueProp, defaultInputValue = "", onInputChange, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, "aria-describedby": describedByProp, "aria-required": ariaRequired }, ref) {
	const generatedId = useId();
	const { id = `ds-multiselect-${generatedId}`, "aria-describedby": describedBy, invalid } = useFormFieldControl({
		id: idProp,
		"aria-describedby": describedByProp,
		invalid: invalidProp
	});
	const listboxId = `${id}-listbox`;
	const countId = `${id}-count`;
	const [openState, setOpen] = useControllable(openProp, defaultOpen, onOpenChange);
	const open = openState && !disabled;
	const [query, setQuery] = useControllable(inputValueProp, defaultInputValue, onInputChange);
	const [activeValue, setActiveValue] = useState(null);
	const [announcement, setAnnouncement] = useState("");
	const boxRef = useRef(null);
	const inputRef = useRef(null);
	const tagButtons = useRef(/* @__PURE__ */ new Map());
	const byValue = useMemo(() => new Map(options.map((option) => [option.value, option])), [options]);
	const indexOf = useMemo(() => new Map(options.map((option, index) => [option.value, index])), [options]);
	const selected = useMemo(() => new Set(value), [value]);
	const filtered = useMemo(() => filterOptions(options, query), [options, query]);
	const enabled = filtered.filter((option) => !option.disabled);
	const active = enabled.some((option) => option.value === activeValue) ? activeValue : null;
	const labelOf = (v) => byValue.get(v)?.label ?? v;
	const optionId = (option) => `${id}-option-${indexOf.get(option.value)}`;
	const [labelText, setLabelText] = useState();
	useLayoutEffect(() => {
		if (open) setLabelText(inputRef.current?.labels?.[0]?.textContent?.trim() || void 0);
	}, [open]);
	const announce = (message) => setAnnouncement((previous) => previous === message ? `${message}\u00a0` : message);
	const countText = (count) => `${count} selected`;
	const toggle = (option) => {
		if (selected.has(option.value)) {
			onChange(value.filter((v) => v !== option.value));
			announce(`${option.label} removed, ${countText(value.length - 1)}`);
		} else {
			onChange([...value, option.value]);
			announce(`${option.label} added, ${countText(value.length + 1)}`);
		}
	};
	const remove = (v) => {
		onChange(value.filter((other) => other !== v));
		announce(`${labelOf(v)} removed, ${countText(value.length - 1)}`);
	};
	const openList = (activate) => {
		if (!open) setOpen(true);
		if (activate) {
			const target = activate === "first" ? enabled[0] : enabled.at(-1);
			setActiveValue(target?.value ?? null);
		}
	};
	const closeList = () => {
		if (open) setOpen(false);
		setActiveValue(null);
	};
	/** Moves the highlight by `step` through the enabled options, stopping at the ends. */
	const move = (step) => setActiveValue(nextActive(enabled, active, step));
	const focusTag = (v) => {
		if (v === void 0) inputRef.current?.focus();
		else tagButtons.current.get(v)?.focus();
	};
	const handleInputKeyDown = (event) => {
		const input = event.currentTarget;
		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				if (!open) openList(event.altKey ? void 0 : "first");
				else move(1);
				break;
			case "ArrowUp":
				event.preventDefault();
				if (event.altKey) closeList();
				else if (!open) openList("last");
				else move(-1);
				break;
			case "Enter": {
				if (!open) break;
				event.preventDefault();
				const option = active === null ? void 0 : byValue.get(active);
				if (option) toggle(option);
				break;
			}
			case "Escape":
				if (open) {
					event.preventDefault();
					closeList();
				} else if (query) {
					event.preventDefault();
					setQuery("");
				}
				break;
			case "Backspace":
				if (input.value === "" && value.length > 0) {
					event.preventDefault();
					remove(value.at(-1));
				}
				break;
			case "ArrowLeft": if (input.selectionStart === 0 && input.selectionEnd === 0 && value.length > 0) {
				event.preventDefault();
				focusTag(value.at(-1));
			}
		}
	};
	const handleTagKeyDown = (event, v) => {
		const at = value.indexOf(v);
		switch (event.key) {
			case "ArrowLeft":
				event.preventDefault();
				focusTag(value[Math.max(at - 1, 0)]);
				break;
			case "ArrowRight":
				event.preventDefault();
				focusTag(value[at + 1]);
				break;
			case "Backspace":
			case "Delete":
				event.preventDefault();
				focusTag(value[at - 1] ?? value[at + 1]);
				remove(v);
		}
	};
	const handleTagClick = (event, v) => {
		const at = value.indexOf(v);
		focusTag(event.detail === 0 ? value[at - 1] ?? value[at + 1] : void 0);
		remove(v);
	};
	const handleBoxMouseDown = (event) => {
		if (disabled || event.target.closest("button")) return;
		const onField = event.target === inputRef.current;
		if (!onField) {
			event.preventDefault();
			inputRef.current?.focus();
		}
		if (!open) openList();
		else if (!onField) closeList();
	};
	const handleChange = (event) => {
		setQuery(event.target.value);
		setActiveValue(null);
		openList();
	};
	const setInput = (element) => {
		inputRef.current = element;
		setRef(ref, element);
	};
	const listboxLabel = ariaLabel ?? (ariaLabelledBy ? void 0 : labelText);
	return /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsxs("div", {
		ref: boxRef,
		className: cx("ds-multiselect", `ds-multiselect--${size}`, invalid && "ds-multiselect--invalid", disabled && "ds-multiselect--disabled", open && "ds-multiselect--open", className),
		onMouseDown: handleBoxMouseDown,
		children: [
			/* @__PURE__ */ jsxs("div", {
				className: "ds-multiselect__values",
				children: [value.map((v) => /* @__PURE__ */ jsxs("span", {
					className: "ds-multiselect__tag",
					children: [/* @__PURE__ */ jsx("span", {
						className: "ds-multiselect__tag-label",
						children: labelOf(v)
					}), !disabled && /* @__PURE__ */ jsx("button", {
						ref: (element) => {
							if (element) tagButtons.current.set(v, element);
							else tagButtons.current.delete(v);
						},
						type: "button",
						tabIndex: -1,
						className: "ds-multiselect__remove",
						"aria-label": `Remove ${labelOf(v)}`,
						onClick: (event) => handleTagClick(event, v),
						onKeyDown: (event) => handleTagKeyDown(event, v),
						children: /* @__PURE__ */ jsx(Icon, {
							name: "x",
							className: "ds-multiselect__remove-icon"
						})
					})]
				}, v)), /* @__PURE__ */ jsx("input", {
					ref: setInput,
					id,
					className: "ds-multiselect__field",
					type: "text",
					role: "combobox",
					autoComplete: "off",
					"aria-autocomplete": "list",
					"aria-expanded": open,
					"aria-controls": open ? listboxId : void 0,
					"aria-activedescendant": open && active !== null ? optionId(byValue.get(active)) : void 0,
					"aria-label": ariaLabel,
					"aria-labelledby": ariaLabelledBy,
					"aria-describedby": cx(value.length > 0 && countId, describedBy) || void 0,
					"aria-invalid": invalid || void 0,
					"aria-required": ariaRequired,
					disabled,
					placeholder: value.length === 0 ? placeholder : void 0,
					value: query,
					onChange: handleChange,
					onKeyDown: handleInputKeyDown,
					onBlur: (event) => {
						if (!boxRef.current?.contains(event.relatedTarget)) closeList();
					}
				})]
			}),
			/* @__PURE__ */ jsx(Icon, {
				name: "chevron-down",
				className: "ds-multiselect__chevron"
			}),
			value.length > 0 && /* @__PURE__ */ jsx(VisuallyHidden, {
				id: countId,
				children: countText(value.length)
			}),
			/* @__PURE__ */ jsx(VisuallyHidden, {
				role: "status",
				children: announcement
			})
		]
	}), open && /* @__PURE__ */ jsx(ListboxPopup, {
		id: listboxId,
		anchorRef: boxRef,
		options: filtered,
		optionId,
		activeValue: active,
		isSelected: (v) => selected.has(v),
		multiple: true,
		emptyMessage,
		onSelect: toggle,
		onActiveChange: setActiveValue,
		onDismiss: closeList,
		"aria-label": listboxLabel,
		"aria-labelledby": ariaLabelledBy
	})] });
});
//#endregion
export { MultiSelect };

//# sourceMappingURL=MultiSelect.js.map